import { toSearchParams } from "@clipboard-health/json-api";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { z } from "zod";

// eslint-disable-next-line no-restricted-syntax
export enum NearbyWorkerStatusLabel {
  FAVORITE = "Favorite",
  RESTRICTED = "Restricted",
  BLOCKED = "Blocked",
}

const NearbyWorkerAttributesSchema = z.object({
  workerId: z.string(),
  phoneNumber: z.string(),
  fullName: z.string(),
  statusLabel: z.nativeEnum(NearbyWorkerStatusLabel).optional(),
  profileImageUrl: z.string().optional(),
});

export type NearbyWorkersAttributesType = z.infer<typeof NearbyWorkerAttributesSchema>;

const NearbyWorkerDataSchema = z.object({
  id: z.string(),
  type: z.literal("agentProfile"),
  attributes: NearbyWorkerAttributesSchema,
});

export type NearbyWorkerDataType = z.infer<typeof NearbyWorkerDataSchema>;

const NearbyWorkersResponseSchema = z.object({
  data: z.array(NearbyWorkerDataSchema),
});

export type NearbyWorkersResponseType = z.infer<typeof NearbyWorkersResponseSchema>;

// FIXME: Automatically disabled during ESLint config update
const GetNearbyWorkersQuerySchema = z.object({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filter: z.object({
    name: z.array(z.string()),
    qualification: z.array(z.string()),
    excludeDNR: z.boolean().optional(),
  }),
  pageSize: z.number().optional(),
});

export type GetNearbyWorkersQuery = z.infer<typeof GetNearbyWorkersQuerySchema>;

export interface GetNearbyWorkersParams {
  workplaceId: string;
  query: GetNearbyWorkersQuery;
}

export function useGetNearbyWorkers(
  params: GetNearbyWorkersParams,
  options?: UseGetQueryOptions<NearbyWorkersResponseType>
) {
  const { workplaceId, query } = params;

  return useGetQuery({
    url: `${
      environmentConfig.REACT_APP_BASE_API_URL
    }/workplaces/${workplaceId}/nearby-workers?${toSearchParams(query)}`,
    responseSchema: NearbyWorkersResponseSchema,
    refetchOnWindowFocus: false,
    ...options,
    enabled: options?.enabled,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_NEARBY_WORKERS_FAILURE,
    },
  });
}
